import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Wifi, ChevronDown, ChevronUp,
  CreditCard, Banknote, ShieldAlert, Clock, Info,
} from "lucide-react";
import { useSettings, SaveBar } from "./shared";

const STATES = {
  not_connected: { label: "Non connecté", cls: "border-slate-200 bg-slate-50", badge: "bg-slate-200 text-slate-600", icon: XCircle, iconCls: "text-slate-400",
    desc: "Vos véhicules ne peuvent pas être réservés avec paiement en ligne tant que Stripe n'est pas configuré." },
  onboarding: { label: "Configuration en cours", cls: "border-blue-200 bg-blue-50", badge: "bg-blue-100 text-blue-700", icon: Clock, iconCls: "text-blue-500",
    desc: "Votre compte Stripe est créé. Terminez la configuration pour activer les encaissements." },
  documents_required: { label: "Documents manquants", cls: "border-amber-200 bg-amber-50", badge: "bg-amber-100 text-amber-700", icon: AlertTriangle, iconCls: "text-amber-500",
    desc: "Stripe attend des justificatifs pour vérifier votre identité. Reprenez l'onboarding pour les fournir." },
  charges_enabled: { label: "Paiements activés", cls: "border-emerald-200 bg-emerald-50", badge: "bg-emerald-100 text-emerald-700", icon: CreditCard, iconCls: "text-emerald-500",
    desc: "Vous pouvez encaisser en ligne. Les virements vers votre banque seront activés après vérification de votre IBAN." },
  payouts_enabled: { label: "Paiements & virements activés", cls: "border-emerald-200 bg-emerald-50", badge: "bg-emerald-100 text-emerald-700", icon: Banknote, iconCls: "text-emerald-600",
    desc: "Votre compte Stripe est totalement opérationnel : encaissements et virements bancaires actifs." },
  restricted: { label: "Compte restreint", cls: "border-red-200 bg-red-50", badge: "bg-red-100 text-red-700", icon: ShieldAlert, iconCls: "text-red-500",
    desc: "Stripe a restreint votre compte : des informations sont requises pour rétablir les encaissements." },
};

const REQ_LABELS = { identity_document: "Pièce d'identité", external_account: "Compte bancaire (IBAN)" };

export default function PaymentsSection() {
  const [connect, setConnect] = useState(null);
  const [busy, setBusy] = useState("");
  const [showInfo, setShowInfo] = useState(false);

  const load = () => api.get("/stripe/connect/status").then((r) => setConnect(r.data)).catch(() => setConnect(null));
  useEffect(() => { load(); }, []);

  if (!connect) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;

  const st = STATES[connect.state] || STATES.not_connected;
  const StIcon = st.icon;

  const startOnboarding = async () => {
    setBusy("onboard");
    try {
      const { data } = await api.post("/stripe/connect/onboarding-link", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); setBusy(""); }
  };

  const refresh = async () => {
    setBusy("refresh");
    try {
      const { data } = await api.post("/stripe/connect/refresh");
      setConnect(data);
      toast.success("Statut Stripe actualisé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setBusy("");
  };

  const testConnection = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/stripe/connect/test");
      (data.ok ? toast.success : toast.warning)(data.message);
    } catch { toast.error("Test impossible"); }
    setBusy("");
  };

  return (
    <div className="max-w-3xl pb-24">
      <div className="overline">Paiements</div>
      <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mt-1">Paiements en ligne</h2>
      <p className="text-sm text-slate-500 mt-2 max-w-2xl">
        Connectez votre compte Stripe pour encaisser directement vos réservations, acomptes, soldes et cautions.
        <span className="ml-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-bold uppercase">Mode {connect.mode === "live" ? "Production" : connect.mode === "sandbox" ? "Sandbox" : "Test simulé"}</span>
      </p>

      {/* Carte statut — 6 états dérivés */}
      <div className={`mt-6 rounded-2xl border p-5 ${st.cls}`} data-testid="stripe-status-card">
        <div className="flex items-start gap-3">
          <StIcon className={`h-5 w-5 shrink-0 mt-0.5 ${st.iconCls}`} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-900">{st.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${st.badge}`} data-testid="stripe-state-badge">{connect.state}</span>
            </div>
            <div className="text-xs text-slate-600 mt-1">{st.desc}</div>
            {connect.requirements_due?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="stripe-requirements">
                {connect.requirements_due.map((r) => (
                  <span key={r} className="px-2 py-0.5 rounded-md bg-white/70 border border-amber-200 text-[10px] font-semibold text-amber-700">{REQ_LABELS[r] || r}</span>
                ))}
              </div>
            )}
            {connect.disabled_reason && (
              <div className="mt-2 text-[11px] font-semibold text-red-600" data-testid="stripe-disabled-reason">Restriction Stripe : {connect.disabled_reason}</div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {connect.state === "not_connected" && (
                <button onClick={startOnboarding} disabled={busy === "onboard"} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-xl bg-[#635BFF] text-white text-xs font-bold hover:bg-[#544ae6] disabled:opacity-50" data-testid="stripe-activate-btn">
                  {busy === "onboard" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Activer les paiements en ligne
                </button>
              )}
              {["onboarding", "documents_required"].includes(connect.state) && (
                <button onClick={startOnboarding} disabled={busy === "onboard"} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 disabled:opacity-50" data-testid="stripe-continue-btn">
                  Continuer la configuration
                </button>
              )}
              {connect.state === "restricted" && (
                <button onClick={startOnboarding} disabled={busy === "onboard"} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-xl bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-50" data-testid="stripe-resume-btn">
                  Reprendre l'onboarding
                </button>
              )}
              {connect.state !== "not_connected" && (
                <>
                  <button onClick={refresh} disabled={busy === "refresh"} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl border border-slate-300 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50" data-testid="stripe-refresh-btn">
                    {busy === "refresh" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Actualiser le statut
                  </button>
                  <button onClick={testConnection} disabled={busy === "test"} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl border border-slate-300 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50" data-testid="stripe-test-btn">
                    {busy === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wifi className="h-3.5 w-3.5" />} Tester la connexion
                  </button>
                  <button onClick={() => setShowInfo(!showInfo)} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl border border-slate-300 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50" data-testid="stripe-info-btn">
                    Voir les informations Stripe {showInfo ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Panneau infos Stripe */}
      {showInfo && connect.state !== "not_connected" && (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-5 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-xs" data-testid="stripe-info-panel">
          <InfoRow label="Mode" value={connect.mode === "live" ? "Production (Live)" : connect.mode === "sandbox" ? "Sandbox (test Stripe)" : "Test simulé"} />
          <InfoRow label="Stripe Account ID" value={connect.stripe_account_id} mono />
          <InfoRow label="Paiements (card_payments)" value={connect.capabilities?.card_payments || "—"} />
          <InfoRow label="Virements (transfers)" value={connect.capabilities?.transfers || "—"} />
          <InfoRow label="Dernière synchronisation" value={fmtDate(connect.last_synced_at)} />
          <InfoRow label="Date d'activation" value={fmtDate(connect.activated_at)} />
          <InfoRow label="Connecté le" value={fmtDate(connect.stripe_connected_at)} />
          <InfoRow label="Restrictions" value={connect.disabled_reason || "Aucune"} />
        </div>
      )}

      <CollectSettings />
    </div>
  );
}

const fmtDate = (s) => (s ? s.slice(0, 16).replace("T", " à ") : "—");

function InfoRow({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`mt-0.5 text-slate-800 ${mono ? "font-mono break-all" : "font-medium"}`}>{value || "—"}</div>
    </div>
  );
}

const COLLECT_MODES = [
  { key: "full", label: "Paiement total", desc: "Le client règle 100 % de la réservation en ligne." },
  { key: "deposit", label: "Acompte", desc: "Le client règle un acompte en ligne, le solde sur place." },
  { key: "on_site", label: "Paiement sur place", desc: "Réservation en ligne sans paiement — encaissement à l'agence." },
];

const CAUTION_MODES = [
  { key: "charge", label: "Caution encaissée", desc: "Le montant est débité puis remboursé après le retour (fonctionne dès maintenant)." },
  { key: "preauth", label: "Empreinte bancaire (pré-autorisation)", desc: "Le montant est bloqué sans débit, capturé seulement en cas de retenue. Pré-autorisation réelle dès l'activation du mode Sandbox/Live." },
];

function CollectSettings() {
  const { settings, loading, reload } = useSettings();
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setLocal({
      online_enabled: settings.payments?.online_enabled !== false,
      collect_mode: settings.payments?.collect_mode || "full",
      caution_mode: settings.payments?.caution_mode || "charge",
      deposit_percent: settings.bookings?.deposit_percent ?? 30,
    });
  }, [settings]);

  if (loading || !local) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>;

  const saved = {
    online_enabled: settings.payments?.online_enabled !== false,
    collect_mode: settings.payments?.collect_mode || "full",
    caution_mode: settings.payments?.caution_mode || "charge",
    deposit_percent: settings.bookings?.deposit_percent ?? 30,
  };
  const dirty = JSON.stringify(local) !== JSON.stringify(saved);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/vendor/settings", {
        payments: { online_enabled: local.online_enabled, collect_mode: local.collect_mode, caution_mode: local.caution_mode },
        bookings: { deposit_percent: parseFloat(local.deposit_percent) || 30 },
      });
      toast.success("Paramètres d'encaissement enregistrés");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="payments-collect-settings">
      <div className="text-sm font-semibold text-slate-900">Encaissement des réservations</div>
      <p className="text-xs text-slate-500 mt-1 mb-5">Ces règles s'appliquent automatiquement à la boutique publique, aux réservations et aux liens de paiement — un seul moteur.</p>

      <label className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 cursor-pointer">
        <div>
          <div className="text-xs font-semibold text-slate-800">Paiements Stripe activés sur la boutique</div>
          <div className="text-[11px] text-slate-400 mt-0.5">Désactivé : vos clients réservent sans payer en ligne.</div>
        </div>
        <button type="button" role="switch" aria-checked={local.online_enabled}
          onClick={() => setLocal((p) => ({ ...p, online_enabled: !p.online_enabled }))}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${local.online_enabled ? "bg-violet-600" : "bg-slate-300"}`}
          data-testid="payments-online-toggle">
          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transform transition-transform ${local.online_enabled ? "translate-x-[18px]" : "translate-x-1"}`} />
        </button>
      </label>

      <div className="mt-5">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Mode d'encaissement</div>
        <div className="space-y-2">
          {COLLECT_MODES.map((m) => (
            <label key={m.key} className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${local.collect_mode === m.key ? "border-violet-400 bg-violet-50/50" : "border-slate-200 hover:border-slate-300"}`}>
              <input type="radio" name="collect_mode" checked={local.collect_mode === m.key}
                onChange={() => setLocal((p) => ({ ...p, collect_mode: m.key }))}
                className="mt-0.5 h-3.5 w-3.5 text-violet-600" data-testid={`collect-mode-${m.key}`} />
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-800">{m.label}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{m.desc}</div>
                {m.key === "deposit" && local.collect_mode === "deposit" && (
                  <div className="mt-2 flex items-center gap-2">
                    <input type="number" min="1" max="99" value={local.deposit_percent}
                      onChange={(e) => setLocal((p) => ({ ...p, deposit_percent: e.target.value }))}
                      className="w-20 h-9 px-2 rounded-lg border border-slate-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                      data-testid="payments-deposit-percent" />
                    <span className="text-[11px] text-slate-500">% du total — même valeur que Réservations → Acompte (source unique).</span>
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Mode de caution</div>
        <div className="space-y-2">
          {CAUTION_MODES.map((m) => (
            <label key={m.key} className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${local.caution_mode === m.key ? "border-violet-400 bg-violet-50/50" : "border-slate-200 hover:border-slate-300"}`}>
              <input type="radio" name="caution_mode" checked={local.caution_mode === m.key}
                onChange={() => setLocal((p) => ({ ...p, caution_mode: m.key }))}
                className="mt-0.5 h-3.5 w-3.5 text-violet-600" data-testid={`caution-mode-${m.key}`} />
              <div>
                <div className="text-xs font-semibold text-slate-800">{m.label}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{m.desc}</div>
              </div>
            </label>
          ))}
        </div>
        {local.caution_mode === "preauth" && (
          <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50/60 px-3.5 py-2.5 text-[11px] text-slate-700" data-testid="caution-preauth-note">
            <Info className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
            En mode test simulé, l'empreinte utilise le flux d'encaissement remboursable. La vraie pré-autorisation (blocage sans débit) sera automatique dès le passage en Sandbox/Live — sans changement de votre côté.
          </div>
        )}
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={save} testid="payments-save" />
    </div>
  );
}
